import { useEffect, useRef, useState } from 'react';

import { readResultsSheet, readResultsWorkbook } from './readResultsWorkbook';
import type { ResultsSheet } from './sheetCells';

export interface DroppedWorkbook {
  fileName: string;
  /** Object URL of the file, for the spreadsheet viewer. */
  fileUrl: string;
  sheet: ResultsSheet;
  /** The sheet being converted. */
  sheetName: string | null;
  /** The sheets that can be picked instead (those with a header row). */
  resultsSheetNames: string[];
  /** The file's bytes, kept so picking another sheet needs no new drop. */
  data: ArrayBuffer;
}

/**
 * The supplier workbook dropped on the page, read once. Conversion is derived
 * from it on render, so switching layout or editing one never re-reads it.
 */
export function useDroppedWorkbook() {
  const [workbook, setWorkbook] = useState<DroppedWorkbook | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  // Only the latest drop may land: an earlier, slower read is discarded.
  const latestRead = useRef(0);

  // The object URL lives as long as its file is on the page.
  const fileUrl = workbook?.fileUrl;
  useEffect(() => {
    if (!fileUrl) return undefined;
    return () => URL.revokeObjectURL(fileUrl);
  }, [fileUrl]);

  async function readFile(file: File): Promise<void> {
    latestRead.current += 1;
    const read = latestRead.current;
    setIsReading(true);
    setReadError(null);
    setWorkbook(null);
    // No `finally`: the React Compiler cannot memoize a try/finally.
    try {
      const contents = await readResultsWorkbook(file);
      if (read !== latestRead.current) return;
      setWorkbook({
        fileName: file.name,
        fileUrl: URL.createObjectURL(file),
        ...contents,
      });
    } catch (error) {
      if (read !== latestRead.current) return;
      setReadError(error instanceof Error ? error.message : String(error));
    }
    setIsReading(false);
  }

  /** Converts another sheet of the same file, e.g. another Division tab. */
  function selectSheet(sheetName: string): void {
    if (!workbook || sheetName === workbook.sheetName) return;
    latestRead.current += 1;
    const read = latestRead.current;
    const current = workbook;
    setIsReading(true);
    setReadError(null);
    // Yields first, so the page shows it is reading before the parse blocks.
    setTimeout(() => {
      if (read !== latestRead.current) return;
      try {
        const sheet = readResultsSheet(current.data, sheetName);
        setWorkbook({ ...current, sheet, sheetName });
      } catch (error) {
        setReadError(error instanceof Error ? error.message : String(error));
      }
      setIsReading(false);
    }, 0);
  }

  /** Forgets the file, e.g. when the chosen supplier needs no second file. */
  function clear(): void {
    latestRead.current += 1;
    setWorkbook(null);
    setReadError(null);
    setIsReading(false);
  }

  return { workbook, isReading, readError, readFile, selectSheet, clear };
}
